import { apiClient } from '../api';

/**
 * Адресная книга пользователя.
 *
 * Бэкенд: routers/address.js, таблица `addresses`. До правки сервер принимал
 * только ярлык, строку адреса и тип, а квартиру/подъезд/этаж/домофон молча
 * выбрасывал — из-за этого адрес приходилось вводить дважды: в профиле и в
 * корзине.
 *
 * Поля приходят в snake_case — ровно так, как лежат в базе; переименовывать
 * нечего, а лишний слой перевода означал бы ещё одно место, где две стороны
 * могут разойтись.
 */

export type AddressType = 'home' | 'work' | 'other';

export const ADDRESS_TYPE_LABELS: Record<AddressType, string> = {
  home: 'Дом',
  work: 'Работа',
  other: 'Другое',
};

/** Больше пяти адресов сервер не хранит (routers/address.js). */
export const MAX_SAVED_ADDRESSES = 5;

export interface SavedAddress {
  id: number;
  label: string;
  address: string;
  type: AddressType;
  apartment: string;
  entrance: string;
  floor: string;
  intercom: string;
  comment: string;
  /** 1 — частный дом: квартиры/подъезда/этажа не спрашиваем. */
  is_private_house: 0 | 1;
  /** 0 — человек нажал «нет домофона»; пустой домофон это выбор, а не пропуск. */
  has_intercom: 0 | 1;
  is_default: 0 | 1;
  lat: number | null;
  lon: number | null;
}

export interface SavedAddressInput {
  label: string;
  address: string;
  type: AddressType;
  apartment?: string;
  entrance?: string;
  floor?: string;
  intercom?: string;
  comment?: string;
  is_private_house?: boolean;
  has_intercom?: boolean;
  is_default?: boolean;
  lat?: number | null;
  lon?: number | null;
}

export const addressesApi = {
  /**
   * Список сохранённых адресов.
   * Сервер отдаёт пустой массив, если таблица ещё не расширена миграцией —
   * интерфейс в этом случае просто не показывает книгу.
   */
  list: async (): Promise<SavedAddress[]> => {
    const rows = await apiClient.get<SavedAddress[]>('/address');
    return Array.isArray(rows) ? rows : [];
  },

  create: async (input: SavedAddressInput): Promise<SavedAddress> => {
    return apiClient.post<SavedAddress>('/address', input);
  },

  update: async (id: number, input: SavedAddressInput): Promise<SavedAddress> => {
    return apiClient.put<SavedAddress>(`/address/${id}`, input);
  },

  remove: async (id: number): Promise<{ message: string }> => {
    return apiClient.delete<{ message: string }>(`/address/${id}`);
  },
};

/**
 * Один и тот же дом, введённый дважды, книгу засорять не должен.
 * Сравниваем по строке адреса и квартире: остальное (этаж, домофон) —
 * уточнения того же места.
 */
export function isSameAddress(
  a: Pick<SavedAddress, 'address' | 'apartment'>,
  b: { address: string; apartment?: string }
): boolean {
  const norm = (value: string | undefined) => (value || '').trim().toLowerCase().replace(/\s+/g, ' ');
  return norm(a.address) === norm(b.address) && norm(a.apartment) === norm(b.apartment);
}

/** Ярлык по умолчанию: первый адрес — «Дом», дальше — по названию улицы. */
export function suggestLabel(address: string, existingCount: number): string {
  if (existingCount === 0) return 'Дом';
  const street = address.split(',').map((part) => part.trim()).filter(Boolean)[1];
  return (street || 'Адрес').slice(0, 40);
}
